import { Navigate, Route, Routes } from 'react-router-dom'
import {
  AlertTriangle,
  BarChart3,
  GalleryHorizontal,
  Tags,
  Gauge,
  Inbox,
  List,
  Mail,
  MessageSquareQuote,
  Plus,
  Package,
  QrCode,
  Plug,
  Settings,
  SlidersHorizontal,
  Sparkles,
  UserCog,
  Users,
  Warehouse,
} from 'lucide-react'
import RequireAuth from '@/routes/RequireAuth'
import ScrollToTop from '@/routes/ScrollToTop'
import LaunchRedirect from '@/routes/LaunchRedirect'
import PortalLayout from '@/layouts/PortalLayout'
import StorefrontLayout from '@/layouts/StorefrontLayout'
import AccountLayout from '@/layouts/AccountLayout'
import StorefrontHome from '@/pages/storefront/Home'
import StorefrontProducts from '@/pages/storefront/Products'
import StorefrontProductDetail from '@/pages/storefront/ProductDetail'
import Cart from '@/pages/storefront/Cart'
import Checkout from '@/pages/storefront/Checkout'
import Track from '@/pages/storefront/Track'
import Login from '@/pages/auth/Login'
import Register from '@/pages/auth/Register'
import ForgotPassword from '@/pages/auth/ForgotPassword'
import ResetPassword from '@/pages/auth/ResetPassword'
import OAuthCallback from '@/pages/auth/OAuthCallback'
import AgentDashboard from '@/pages/agent/Dashboard'
import Orders from '@/pages/agent/Orders'
import OrderDetail from '@/pages/agent/OrderDetail'
import Wallet from '@/pages/agent/Wallet'
import TopUp from '@/pages/agent/TopUp'
import AccountSettings from '@/pages/agent/Settings'
import AccountAddresses from '@/pages/agent/Addresses'
import TopUpReturn from '@/pages/agent/TopUpReturn'
import OrderPaymentReturn from '@/pages/agent/OrderPaymentReturn'
import StaffScan from '@/pages/staff/Scan'
import StaffJobs from '@/pages/staff/Jobs'
import StaffJobDetail from '@/pages/staff/JobDetail'
import StaffStats from '@/pages/staff/Stats'
import PartnerJobs from '@/pages/partner/Jobs'
import PartnerJobDetail from '@/pages/partner/JobDetail'
import Profile from '@/pages/staff/Profile'
import AdminDashboard from '@/pages/admin/Dashboard'
import AdminCustomers from '@/pages/admin/Customers'
import AdminStaff from '@/pages/admin/Staff'
import AdminIntegrations from '@/pages/admin/Integrations'
import AdminOrders from '@/pages/admin/Orders'
import AdminOrderDetail from '@/pages/admin/OrderDetail'
import AdminProducts from '@/pages/admin/Products'
import AdminProductEditor from '@/pages/admin/ProductEditor'
import AdminProductOptions from '@/pages/admin/ProductOptions'
import AdminBrandLogos from '@/pages/admin/BrandLogos'
import AdminTestimonials from '@/pages/admin/Testimonials'
import AdminHero from '@/pages/admin/Hero'
import AdminCreateOrder from '@/pages/admin/CreateOrder'
import SalesOrders from '@/pages/sales/Orders'
import SalesOrderDetail from '@/pages/sales/OrderDetail'
import SalesCreateOrder from '@/pages/sales/CreateOrder'
import SalesCustomers from '@/pages/sales/Customers'
import SalesReports from '@/pages/sales/Reports'
import AdminJobs from '@/pages/admin/Jobs'
import JobSheet from '@/pages/admin/JobSheet'
import AdminCapacity from '@/pages/admin/Capacity'
import AdminDelivery from '@/pages/admin/Delivery'
import AdminPricing from '@/pages/admin/Pricing'
import AdminSla from '@/pages/admin/Sla'
import AdminRouting from '@/pages/admin/Routing'
import AdminEmailLog from '@/pages/admin/EmailLog'
import AdminReports from '@/pages/admin/Reports'
import AdminAi from '@/pages/admin/Ai'
import { aiFeatures } from '@/lib/aiFeatures'
import AdminPartners from '@/pages/admin/Partners'
import AdminIssues from '@/pages/admin/Issues'
import AdminIssueReasons from '@/pages/admin/settings/IssueReasons'
import AdminChecklist from '@/pages/admin/settings/Checklist'
import AdminNotifications from '@/pages/admin/settings/Notifications'
import AdminFlows from '@/pages/admin/Flows'
import SettingsLayout from '@/pages/admin/settings/SettingsLayout'
import AdminInvoiceSettings from '@/pages/admin/settings/Invoice'
import InvoicePage from '@/pages/shared/InvoicePage'

const iconClass = 'size-4'

const staffNav = [
  { to: '/staff', label: 'Scan / Queue', tabLabel: 'Scan', center: true, icon: <QrCode className={iconClass} />, end: true },
  { to: '/staff/jobs', label: 'My Jobs', icon: <Inbox className={iconClass} /> },
  { to: '/staff/stats', label: 'My Stats', icon: <BarChart3 className={iconClass} /> },
  { to: '/staff/profile', label: 'My Account', icon: <UserCog className={iconClass} /> },
]

const partnerNav = [
  { to: '/partner', label: 'Jobs', icon: <Inbox className={iconClass} />, end: true },
  { to: '/partner/profile', label: 'My Account', icon: <UserCog className={iconClass} /> },
]

// No badge keys: the issues count endpoint is admin only, and useNavBadges
// only fetches for a nav item that asks for one.
const salesNav = [
  { to: '/sales', label: 'Orders', icon: <Inbox className={iconClass} />, end: true, badge: 'pending' as const },
  { to: '/sales/orders/new', label: 'New order', icon: <Plus className={iconClass} /> },
  { to: '/sales/customers', label: 'Customers', icon: <Users className={iconClass} /> },
  { to: '/sales/reports', label: 'Reports', icon: <BarChart3 className={iconClass} /> },
  { to: '/sales/profile', label: 'My Account', icon: <UserCog className={iconClass} /> },
]

// Ordered by how often an admin opens the screen: the daily work sits directly
// under Dashboard, so the Issues badge is seen and Orders is one glance away.
const adminNav = [
  { to: '/admin', label: 'Dashboard', icon: <Gauge className={iconClass} />, end: true },
  { to: '/admin/orders', label: 'Orders', icon: <Inbox className={iconClass} />, badge: 'pending' as const },
  { to: '/admin/jobs', label: 'Jobs', icon: <QrCode className={iconClass} /> },
  { to: '/admin/issues', label: 'Issues', icon: <AlertTriangle className={iconClass} />, badge: 'issues' as const },
  { to: '/admin/customers', label: 'Customers', icon: <Users className={iconClass} /> },
  {
    to: '/admin/products',
    label: 'Products',
    icon: <Package className={iconClass} />,
    // Opens into the product list and the option groups products offer.
    children: [
      // The product editor (/admin/products/new, /admin/products/:id) is part of the list.
      { to: '/admin/products', label: 'All Products', icon: <List className={iconClass} />, end: true, also: /^\/admin\/products\/(new|\d+)$/ },
      { to: '/admin/products/options', label: 'Product Options', icon: <SlidersHorizontal className={iconClass} /> },
    ],
  },
  { to: '/admin/partners', label: 'Warehouse Partners', icon: <Warehouse className={iconClass} /> },
  { to: '/admin/reports', label: 'Reports', icon: <BarChart3 className={iconClass} /> },
  // The planning assistant (Duncan 2026-10-26). Admin only: never in salesNav.
  // Off the sidebar until it goes live (lib/aiFeatures); the URL still opens.
  { to: '/admin/ai', label: 'MPG AI', icon: <Sparkles className={iconClass} />, hidden: !aiFeatures.chat },
  { to: '/admin/staff', label: 'Staff & Admins', icon: <UserCog className={iconClass} /> },
  // Off the sidebar, still reachable by URL. Routing suggestions are no
  // longer generated - jobs are handed to partners from the order page.
  { to: '/admin/routing', label: 'Warehouse Routing', icon: <Warehouse className={iconClass} />, hidden: true },
  { to: '/admin/email-log', label: 'Email Log', icon: <Mail className={iconClass} />, hidden: true },
  {
    to: '/admin/hero',
    label: 'Hero Carousel',
    icon: <GalleryHorizontal className={iconClass} />,
    section: 'Landing Page Setup',
  },
  {
    to: '/admin/brand-logos',
    label: 'Brand Logos',
    icon: <Tags className={iconClass} />,
    section: 'Landing Page Setup',
  },
  // Must stay next to the other Landing Page Setup items: the sidebar prints a
  // section heading whenever the section changes from the item above.
  {
    to: '/admin/testimonials',
    label: 'Testimonials',
    icon: <MessageSquareQuote className={iconClass} />,
    section: 'Landing Page Setup',
  },
  { to: '/admin/integrations', label: 'Integrations', icon: <Plug className={iconClass} />, section: 'System' },
  { to: '/admin/settings', label: 'Settings', icon: <Settings className={iconClass} />, section: 'System' },
]

export default function App() {
  return (
    <>
      <ScrollToTop />
      <LaunchRedirect />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />

      {/* Public marketplace - configure and cart without an account, sign in at checkout */}
      <Route element={<StorefrontLayout />}>
        <Route path="/" element={<StorefrontHome />} />
        <Route path="/products" element={<StorefrontProducts />} />
        <Route path="/p/:id" element={<StorefrontProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        {/* Opened from a shared link only - there is no landing page to browse to */}
        <Route path="/track/:code" element={<Track />} />
        <Route element={<RequireAuth role="customer" />}>
          <Route path="/checkout" element={<Checkout />} />
          {/* Customer account keeps the storefront chrome - navbar, cart badge
              and footer stay visible; AccountLayout adds the tab row. */}
          <Route element={<AccountLayout />}>
            <Route path="/account" element={<AgentDashboard />} />
            <Route path="/account/orders" element={<Orders />} />
            <Route path="/account/orders/payment/return" element={<OrderPaymentReturn />} />
            <Route path="/account/orders/:id" element={<OrderDetail />} />
            <Route path="/account/wallet" element={<Wallet />} />
            <Route path="/account/wallet/topup" element={<TopUp />} />
            <Route path="/account/wallet/topup/return" element={<TopUpReturn />} />
            <Route path="/account/addresses" element={<AccountAddresses />} />
            <Route path="/account/settings" element={<AccountSettings />} />
          </Route>
        </Route>
      </Route>

      {/* The printable invoice, outside the storefront chrome like the job sheet. */}
      <Route element={<RequireAuth role="customer" />}>
        <Route path="/account/orders/:id/invoice" element={<InvoicePage basePath="" />} />
      </Route>

      {/* Ordering moved to the marketplace - keep old portal URLs working */}
      <Route path="/account/products" element={<Navigate to="/products" replace />} />
      <Route path="/account/orders/new" element={<Navigate to="/products" replace />} />

      <Route element={<RequireAuth role="staff" />}>
        <Route path="/staff/jobs/:id/sheet" element={<JobSheet />} />
        <Route element={<PortalLayout nav={staffNav} title="Production Floor" bottomNav />}>
          <Route path="/staff" element={<StaffScan />} />
          <Route path="/staff/jobs" element={<StaffJobs />} />
          <Route path="/staff/jobs/:token" element={<StaffJobDetail />} />
          <Route path="/staff/stats" element={<StaffStats />} />
          <Route path="/staff/profile" element={<Profile />} />
        </Route>
      </Route>

      <Route element={<RequireAuth role="partner" />}>
        <Route path="/partner/jobs/:id/sheet" element={<JobSheet />} />
        <Route element={<PortalLayout nav={partnerNav} title="Warehouse Partner Portal" />}>
          <Route path="/partner" element={<PartnerJobs />} />
          <Route path="/partner/jobs/:token" element={<PartnerJobDetail />} />
          <Route path="/partner/profile" element={<Profile />} />
        </Route>
      </Route>

      {/* No /sales/jobs/:id/sheet: sales does not print job sheets, and the
          admin sheet endpoint is not in their route group either. */}
      <Route element={<RequireAuth role="sales" />}>
        <Route path="/sales/orders/:id/invoice" element={<InvoicePage basePath="/sales" />} />
        <Route element={<PortalLayout nav={salesNav} title="Sales Console" />}>
          <Route path="/sales" element={<SalesOrders />} />
          <Route path="/sales/orders/new" element={<SalesCreateOrder />} />
          <Route path="/sales/orders/:id" element={<SalesOrderDetail />} />
          <Route path="/sales/customers" element={<SalesCustomers />} />
          <Route path="/sales/reports" element={<SalesReports />} />
          <Route path="/sales/profile" element={<Profile />} />
        </Route>
      </Route>

      <Route element={<RequireAuth role="admin" />}>
        <Route path="/admin/jobs/:id/sheet" element={<JobSheet />} />
        <Route path="/admin/orders/:id/invoice" element={<InvoicePage basePath="/admin" />} />
        <Route element={<PortalLayout nav={adminNav} title="Admin Console" />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/customers" element={<AdminCustomers />} />
          <Route path="/admin/staff" element={<AdminStaff />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/products/options" element={<AdminProductOptions />} />
          {/* key: from one product straight to New gives a fresh form, not the old draft. */}
          <Route path="/admin/products/new" element={<AdminProductEditor key="new" />} />
          <Route path="/admin/products/:id" element={<AdminProductEditor />} />
          <Route path="/admin/hero" element={<AdminHero />} />
          <Route path="/admin/brand-logos" element={<AdminBrandLogos />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/orders/new" element={<AdminCreateOrder />} />
          <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
          <Route path="/admin/jobs" element={<AdminJobs />} />
          <Route path="/admin/issues" element={<AdminIssues />} />
          <Route path="/admin/settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="capacity" replace />} />
            <Route path="capacity" element={<AdminCapacity />} />
            <Route path="delivery" element={<AdminDelivery />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="sla" element={<AdminSla />} />
            <Route path="flows" element={<AdminFlows />} />
            <Route path="issue-reasons" element={<AdminIssueReasons />} />
            <Route path="checklist" element={<AdminChecklist />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="invoice" element={<AdminInvoiceSettings />} />
          </Route>
          {/* Config pages moved under Settings - keep the old flat URLs working */}
          <Route path="/admin/capacity" element={<Navigate to="/admin/settings/capacity" replace />} />
          <Route path="/admin/delivery" element={<Navigate to="/admin/settings/delivery" replace />} />
          <Route path="/admin/pricing" element={<Navigate to="/admin/settings/pricing" replace />} />
          <Route path="/admin/sla" element={<Navigate to="/admin/settings/sla" replace />} />
          <Route path="/admin/flows" element={<Navigate to="/admin/settings/flows" replace />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/ai" element={<AdminAi />} />
          <Route path="/admin/partners" element={<AdminPartners />} />
          <Route path="/admin/routing" element={<AdminRouting />} />
          <Route path="/admin/email-log" element={<AdminEmailLog />} />
          <Route path="/admin/integrations" element={<AdminIntegrations />} />
          <Route path="/admin/profile" element={<Profile />} />
        </Route>
      </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
